import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { rbac } from '@dha-nexus/shared';
import { api } from '../../lib/apiClient.js';
import { useClientPagination } from '../../lib/usePagination.js';
import {
  AiCitationStrip,
  AiGroundingChip,
  PageViewerControls,
  KpiHeroBand,
  deriveKpiTone,
  BrandedEmptyState,
  LoadingSkeleton,
  toast,
  activityRail,
} from '../../components/index.js';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import {
  ProvenanceBadge,
  normalizedProvenance,
} from '../../components/ProvenanceBadge.jsx';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { PageHeader } from '../../app/PageHeader.jsx';
import { buildRouteContext } from '../../lib/routeContext.js';
import { personaKeyFromRoles, primaryRoleLabel } from '../../lib/personaKey.js';
import './ai.css';
import './ai.redesign.css';

// Mirror of the server's AI_REVIEW_ROLES gate on POST /ai/ask/escalate — only
// briefing reviewers may escalate an answer into a draft product, so the
// action is hidden (not just failing) for everyone else.
const ESCALATE_ROLES = new Set([
  rbac.ROLE_IDS.EXECUTIVE_SPONSOR,
  rbac.ROLE_IDS.PORTFOLIO_MANAGER,
  rbac.ROLE_IDS.PROGRAM_MANAGER,
  rbac.ROLE_IDS.PROJECT_MANAGER,
]);

const SUGGESTIONS_BY_PERSONA = {
  'Executive Sponsor': [
    'Summarize the top three portfolio risks driving red status today.',
    'What is the Gate 3 readiness status for Sentinel Guard?',
    'Which AI runs are pending my review?',
    'Which portfolios offer the highest mission impact per dollar?',
  ],
  'Portfolio Manager': [
    'Which blockers threaten the AI Diagnostic Module IOC?',
    'Show forecast variance by portfolio.',
    'Which projects have unresolved lineage conflicts?',
    'Where are dependencies slipping vs sprint plan?',
  ],
  'Data Steward': [
    'Which connectors failed their last sync?',
    'Where do PMRT and warehouse forecasts disagree?',
    'List recent manual overrides on lineage.',
    'Which data quality findings need triage today?',
  ],
  default: [
    'What is the current state of the portfolio?',
    'Show me the most recent AI briefings.',
    'Which access requests need review?',
    'What is the most important alert right now?',
  ],
};

// Map a 0..1 model confidence onto the executive traffic-light tones so the
// per-answer assurance band reads the same as every other governed KPI.
function confidenceTone(confidence) {
  if (typeof confidence !== 'number') return 'slate';
  if (confidence >= 0.85) return 'sage';
  if (confidence >= 0.75) return 'amber';
  return 'brick';
}

function confidenceLabel(confidence) {
  if (typeof confidence !== 'number') return 'Unscored';
  if (confidence >= 0.85) return 'High assurance';
  if (confidence >= 0.75) return 'Review advised';
  return 'Low assurance';
}

const CONFIDENCE_SOURCE_LABELS = {
  heuristic: 'Heuristic confidence',
  rules: 'Rules confidence',
  model: 'Model confidence',
  authored: 'Authored confidence',
  unknown: 'Confidence · source unknown',
};

const CITATION_SCORE_LABELS = {
  keyword_heuristic: 'Keyword match strength',
  provider_retrieval: 'Provider retrieval score',
  authored: 'Authored source score',
  none: 'Source score · source unknown',
};

// Governed-run review states → executive-readable labels and tones. Mirrors
// the review vocabulary used by the orchestration queue so the oversight tile
// reads the same everywhere.
const REVIEW_STATE_LABELS = {
  pending_human_review: 'Pending review',
  needs_rework: 'Needs rework',
  approved_for_export: 'Approved',
  human_reviewed: 'Reviewed',
  reject: 'Rejected',
  rejected: 'Rejected',
};

const REVIEW_STATE_TONES = {
  pending_human_review: 'amber',
  needs_rework: 'amber',
  approved_for_export: 'sage',
  human_reviewed: 'blue',
  reject: 'brick',
  rejected: 'brick',
};

// Derive the per-answer confidence / grounding KPI band purely from data the
// /ai/ask (or /ai/ask-history) response already carried — never fabricated.
// Returns null when there is nothing trustworthy to summarize.
function buildAssuranceItems(data) {
  if (!data) return [];
  const provenance = normalizedProvenance(data, 'ai');
  const citations = data.citations || [];
  const scores = citations
    .map((c) => (typeof c?.score === 'number' ? c.score : null))
    .filter((s) => s != null);
  const topScore = scores.length ? Math.max(...scores) : null;
  const items = [];

  if (typeof provenance.confidence.value === 'number') {
    const cTone = confidenceTone(provenance.confidence.value);
    items.push({
      id: 'confidence',
      label:
        CONFIDENCE_SOURCE_LABELS[provenance.confidence.source] ||
        CONFIDENCE_SOURCE_LABELS.unknown,
      value: Math.round(provenance.confidence.value * 100),
      unit: '%',
      status: cTone,
      statusLabel: confidenceLabel(provenance.confidence.value),
      footer: `Source: ${provenance.confidence.source}`,
    });
  }

  items.push({
    id: 'citations',
    label: 'Cited sources',
    value: citations.length,
    status: deriveKpiTone({ value: citations.length, target: 3 }),
    footer: citations.length
      ? 'Each chip resolves to a source artifact'
      : 'No source artifacts attached',
  });

  if (topScore != null) {
    items.push({
      id: 'top-relevance',
      label:
        CITATION_SCORE_LABELS[provenance.citationScoreSource] || CITATION_SCORE_LABELS.none,
      value: Number(topScore).toFixed(2),
      status: confidenceTone(topScore),
      footer: `Score source: ${provenance.citationScoreSource}`,
    });
  }

  // Oversight tile (TT-09 / AC-ASK-3): bound to the review state the payload
  // actually carries. When the answer has no governed run at all, say so;
  // when the run exists but the payload reports no review state, omit the
  // tile rather than assert one that was never returned.
  const reviewState = data.reviewState || data.humanReviewState || null;
  const modeFooter = `${provenance.actualProvider} · ${provenance.executionType} execution`;
  if (reviewState) {
    items.push({
      id: 'oversight',
      label: 'Oversight',
      value: REVIEW_STATE_LABELS[reviewState] || String(reviewState).replace(/_/g, ' '),
      status: REVIEW_STATE_TONES[reviewState] || 'slate',
      statusLabel: 'Human review',
      footer: modeFooter,
    });
  } else if (!data.runId) {
    items.push({
      id: 'oversight',
      label: 'Oversight',
      value: 'No run yet',
      status: 'slate',
      statusLabel: 'Not reviewable',
      footer: modeFooter,
    });
  }

  return items;
}

// Render a stored answer as formatted prose (FS-AI-3: formatted, not <pre>).
// Paragraph breaks are honored; single newlines inside a paragraph become
// line breaks so tabular-ish model output still reads correctly.
function AnswerProse({ answer }) {
  const paragraphs = String(answer || '')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  if (!paragraphs.length) return null;
  return (
    <div className="ai-ask-answer">
      {paragraphs.map((paragraph, index) => (
        <p key={index}>
          {paragraph.split('\n').map((line, lineIndex, lines) => (
            <span key={lineIndex}>
              {line}
              {lineIndex < lines.length - 1 ? <br /> : null}
            </span>
          ))}
        </p>
      ))}
    </div>
  );
}

export function AiAskPage() {
  const currentUser = useCurrentUser();
  const { user, roles = [] } = currentUser;
  const location = useLocation();
  const navigate = useNavigate();
  const canEscalate = roles.some((role) => ESCALATE_ROLES.has(role));
  // The answer card pending escalation confirmation (AC-ASK-4).
  const [escalating, setEscalating] = useState(null);
  const [escalateBusy, setEscalateBusy] = useState(false);
  // Roles decide the suggestion set; the title string is only a legacy
  // fallback (DEF-002 — a Program Manager account must not read "default").
  const personaBucket = roles.length ? personaKeyFromRoles(roles) : null;
  const personaKey = personaBucket
    ? {
        executive_sponsor: 'Executive Sponsor',
        delivery_leader: 'Portfolio Manager',
        data_steward: 'Data Steward',
      }[personaBucket] || 'default'
    : (user?.title || '').includes('Executive')
      ? 'Executive Sponsor'
      : (user?.title || '').includes('Portfolio')
        ? 'Portfolio Manager'
        : (user?.title || '').includes('Steward')
          ? 'Data Steward'
          : 'default';
  const suggestionLabel = primaryRoleLabel(roles) || (personaKey !== 'default' ? personaKey : null);
  const suggestions = SUGGESTIONS_BY_PERSONA[personaKey] || SUGGESTIONS_BY_PERSONA.default;
  const [question, setQuestion] = useState(
    'Which blockers threaten the AI Diagnostic Module IOC, and which source artifacts support the answer?',
  );
  const [busy, setBusy] = useState(false);
  // The question currently being grounded, shown in the skeleton placeholder.
  const [pendingQuestion, setPendingQuestion] = useState(null);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState(null);
  const historyPager = useClientPagination(history, {
    initialPageSize: 6,
    resetKey: `history-${history.length}`,
  });

  // Pre-populate the rail with seeded Q&A history so /ai/ask never lands on
  // an empty state for first-time demo viewers.  Seeded entries are read-only
  // examples; new asks prepend to this list in memory.
  useEffect(() => {
    let cancelled = false;
    api
      .get('/ai/ask-history')
      .then((res) => {
        if (cancelled) return;
        const entries = (res?.data || []).map((entry) => ({
          q: entry.question,
          data: {
            answer: entry.answer,
            citations: entry.citations || [],
            confidence: entry.confidence,
            confidenceSource: entry.confidenceSource,
            citationScoreSource: entry.citationScoreSource,
            mode: entry.mode,
            modelId: entry.modelId ?? null,
            modelInvoked: entry.modelInvoked,
            provider: entry.provider,
            provenance: entry.provenance,
            groundedIn: entry.groundedIn || null,
            reviewState: entry.reviewState || entry.humanReviewState || null,
            runId: entry.id,
            scopeType: entry.scopeType || null,
            scopeId: entry.scopeId || null,
            seeded: entry.seeded,
            illustrative: entry.illustrative,
          },
        }));
        setHistory((current) => (current.length ? current : entries));
      })
      .catch(() => {
        // Silent — first-load enrichment is non-critical.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function ask(value) {
    const q = (value ?? question).trim();
    if (!q) return;
    setBusy(true);
    setPendingQuestion(q);
    setErr(null);
    const context = buildRouteContext({ location, currentUser });
    try {
      const res = await api.post('/ai/ask', { question: q, context });
      const data = res.data || res;
      const answerProvenance = normalizedProvenance(data, 'ai');
      setHistory((h) => [{ q, data }, ...h]);
      setQuestion('');
      toast.success({
        title: 'Answer ready',
        body: `${data.citations?.length || 0} citations · ${
          typeof data.confidence === 'number'
            ? `${Math.round(data.confidence * 100)}% ${(
                CONFIDENCE_SOURCE_LABELS[answerProvenance.confidence.source] ||
                CONFIDENCE_SOURCE_LABELS.unknown
              ).toLowerCase()} · `
            : ''
        }execution provenance attached`,
      });
      activityRail.push({
        action: 'ai.ask',
        targetType: 'ai_run',
        targetId: data.runId || 'AIRUN-MOCK',
        actorUserId: user?.id || 'unknown',
        summary: q.slice(0, 96),
      });
    } catch (e) {
      setErr(e);
      toast.error({ title: 'Ask failed', body: e.message });
    } finally {
      setBusy(false);
      setPendingQuestion(null);
    }
  }

  // AC-ASK-4: hand the answer + citations to the product factory as a DRAFT
  // briefing. The server creates the draft (never approved, never exported)
  // and a named human must still approve it before export — the AI law holds.
  async function escalateAnswer(entry) {
    setEscalateBusy(true);
    try {
      const res = await api.post('/ai/ask/escalate', {
        runId: entry.data.runId || null,
        question: entry.q,
        answer: entry.data.answer,
        citations: entry.data.citations || [],
        scopeType: entry.data.scopeType || null,
        scopeId: entry.data.scopeId || null,
      });
      const payload = res?.data?.data || res?.data || res;
      toast.success({
        title: 'Draft briefing created',
        body: 'The answer, its scope, and its citations were carried into a DRAFT product awaiting human review.',
      });
      activityRail.push({
        action: 'briefing_product.escalate_from_ask',
        targetType: 'briefing_product',
        targetId: payload.briefingProductId || 'BRF-NEW',
        actorUserId: user?.id || 'unknown',
        summary: entry.q.slice(0, 96),
      });
      setEscalating(null);
      if (payload.briefingProductId) {
        navigate(`/briefings/${payload.briefingProductId}`);
      }
    } catch (e) {
      toast.error({ title: 'Escalation failed', body: e.message });
    } finally {
      setEscalateBusy(false);
    }
  }

  return (
    <>
      {/* PRIME-SHELL-UX-001: single body page identity + relocated About. */}
      <PageHeader />
      <div className="ai-ask-shell">
        <form
          className="ai-ask-form"
          onSubmit={(e) => {
            e.preventDefault();
            ask();
          }}
        >
          <div className="ai-ask-form-head">
            <span className="ai-ask-form-eyebrow">Ask PRIME</span>
            <p>
              Use plain language. Every answer is grounded in your portfolio and cites the source
              artifacts it used.
            </p>
          </div>
          <textarea
            rows={5}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="What do you want to ask…"
            data-testid="ai-ask-input"
          />
          <div className="ai-ask-form-foot">
            <span
              className="chip amber sm ai-ask-review-chip"
              title="AI answers route through human review before they drive a decision."
            >
              <span className="chip-dot" />
              Pending human review
            </span>
            <button
              className="btn tonal tactile-press"
              disabled={busy || !question.trim()}
              data-testid="ai-ask-submit"
            >
              {busy ? 'Thinking…' : 'Ask PRIME'}
            </button>
          </div>
          {err ? <div className="banner red">{err.message}</div> : null}
        </form>

        <aside className="card ai-ask-suggestions">
          <div className="eyebrow">
            {suggestionLabel ? `Suggested for ${suggestionLabel}` : 'Suggested questions'}
          </div>
          <div className="ai-ask-suggestion-grid">
            {suggestions.map((s) => (
              <button key={s} className="ai-ask-suggestion" onClick={() => ask(s)} disabled={busy}>
                {s}
              </button>
            ))}
          </div>
        </aside>
      </div>

      {busy ? (
        // FS-AI-3: skeleton answer placeholder while the model grounds a reply.
        <article
          className="ai-ask-card"
          data-testid="ai-ask-pending"
          aria-busy="true"
          style={{ marginTop: 'var(--space-3)' }}
        >
          <div className="ai-ask-card-head">
            <p className="ai-ask-question">{pendingQuestion}</p>
          </div>
          <LoadingSkeleton rows={2} />
          <p className="muted small">PRIME is preparing an answer and its provenance…</p>
        </article>
      ) : null}

      {history.length === 0 && !busy ? (
        <div className="ai-ask-intro" data-testid="ai-ask-empty">
          <BrandedEmptyState
            icon="◎"
            eyebrow="Grounded portfolio intelligence"
            title="Ask anything about your portfolio"
            description="Pick a suggested prompt or type your own. Every answer carries a confidence band, the sources it relied on, and routes through human review before it drives a decision."
            tone="slate"
          />
          <div className="ai-ask-intro-rails" aria-hidden="true">
            <div className="ai-ask-intro-rail">
              <span className="ai-ask-intro-rail-icon">①</span>
              <div>
                <strong>Ask in plain language</strong>
                <span>No query syntax. PRIME knows which page you came from.</span>
              </div>
            </div>
            <div className="ai-ask-intro-rail">
              <span className="ai-ask-intro-rail-icon">②</span>
              <div>
                <strong>See the assurance band</strong>
                <span>Sourced confidence, cited sources, and the strongest match.</span>
              </div>
            </div>
            <div className="ai-ask-intro-rail">
              <span className="ai-ask-intro-rail-icon">③</span>
              <div>
                <strong>Trace every claim</strong>
                <span>Citation chips resolve straight to source artifacts.</span>
              </div>
            </div>
          </div>
        </div>
      ) : history.length ? (
        <div className="ai-ask-history">
          {historyPager.items.map((h, i) => {
            const assuranceItems = buildAssuranceItems(h.data);
            const provenance = normalizedProvenance(h.data, 'ai');
            return (
              <article key={i} className="ai-ask-card" data-testid="ai-ask-card">
                <div className="ai-ask-card-head">
                  <p className="ai-ask-question">{h.q}</p>
                  {h.data.groundedIn ? <AiGroundingChip groundedIn={h.data.groundedIn} /> : null}
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <ProvenanceBadge value={h.data} kind="ai" />
                    {provenance.seeded || provenance.illustrative ? (
                      <span className="muted small">Seeded · illustrative example</span>
                    ) : null}
                  </div>
                </div>
                {assuranceItems.length ? (
                  <KpiHeroBand
                    className="ai-ask-assurance"
                    eyebrow="Answer assurance"
                    items={assuranceItems}
                    columns={assuranceItems.length}
                  />
                ) : null}
                <AnswerProse answer={h.data.answer} />
                {h.data.citations?.length ? (
                  <div className="ai-ask-citations">
                    <div className="eyebrow">Source citations</div>
                    {/* Chips navigate to the cited record (AC-TRUST-3); unresolvable
                        sources render inert with "No linked record view". */}
                    <AiCitationStrip citations={h.data.citations} />
                  </div>
                ) : null}
                <div className="row spread ai-ask-card-foot" style={{ gap: 10, flexWrap: 'wrap' }}>
                  <p className="muted small ai-ask-card-model" style={{ margin: 0 }}>
                    {provenance.executionType === 'rule' ? (
                      <>
                        rules engine{' '}
                        <span className="mono">
                          {provenance.rulesEngine.id} v{provenance.rulesEngine.version}
                        </span>
                      </>
                    ) : provenance.modelInvoked ? (
                      <>
                        model <span className="mono">{provenance.modelId}</span>
                      </>
                    ) : (
                      <>{provenance.actualProvider} · no model invoked</>
                    )}
                  </p>
                  <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
                    {h.data.runId ? (
                      // AC-ASK-1: every answer links its governed run dossier.
                      <Link className="btn link sm" to={`/ai/runs/${h.data.runId}`}>
                        Governed run {h.data.runId}
                      </Link>
                    ) : null}
                    {canEscalate && h.data.answer ? (
                      <button
                        type="button"
                        className="btn outline sm"
                        onClick={() => setEscalating(h)}
                        disabled={escalateBusy}
                      >
                        Escalate to briefing
                      </button>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
          <PageViewerControls
            page={historyPager.page}
            label="answers"
            pageSize={historyPager.pageSize}
            onOffsetChange={historyPager.setOffset}
            onPageSizeChange={historyPager.setPageSize}
          />
        </div>
      ) : null}
      <ConfirmActionDialog
        open={Boolean(escalating)}
        title="Escalate this answer to a draft briefing?"
        body="This creates a DRAFT information paper in the Briefing Product Factory carrying the answer, its scope, and its citations. A named human reviewer must approve the draft before it can be exported — escalation never approves or publishes anything."
        confirmLabel="Escalate to draft"
        busy={escalateBusy}
        onConfirm={() => escalating && escalateAnswer(escalating)}
        onCancel={() => setEscalating(null)}
      />
    </>
  );
}
